import { CommonModule } from '@angular/common';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { DsyTagConf } from '../../../models/dsy-tabs.model';
import { ProgramOrganizationType } from '../../../models/program-organization.model';
import { BusinessRole } from '../../../models/role.model';
import { setActiveTab, setViewMode } from '../../../store/actions/pd.actions';
import {
  selectActiveTabIndex,
  selectBreadcrumbs,
  selectHasBusinessRole,
  selectProducts,
  selectRoles,
  selectRolesLoaded,
  selectSites,
  selectViewMode,
} from '../../../store/selectors/pd.selectors';
import { RoutesName } from '../../shared/constants/routes-name';
import { applyCustomBreadcrumbsCss } from '../../shared/format-styles/breadcrumbs-styles';

@Component({
  selector: 'fdr-past-performance-ui-home',
  imports: [RouterOutlet, CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });
  readonly hasPerformanceAdminRole = this.store.selectSignal(selectHasBusinessRole(BusinessRole.PerformanceAdmin));
  readonly hasUtilityDispatchUserRole = this.store.selectSignal(selectHasBusinessRole(BusinessRole.UtilityDispatchUser));
  readonly showSwitcher = computed(() => this.hasPerformanceAdminRole() || this.hasUtilityDispatchUserRole());
  readonly roles = this.store.selectSignal(selectRoles);
  readonly rolesLoaded = this.store.selectSignal(selectRolesLoaded);

  readonly selectedTabIndex = this.store.selectSignal(selectActiveTabIndex);
  readonly destroyRef = inject(DestroyRef);

  // Define tabs configuration first, so it's available in ngOnInit
  private readonly breadcrumbs = this.store.selectSignal(selectBreadcrumbs);
  private readonly sites = this.store.selectSignal(selectSites);
  private readonly siteNameMap = computed(() => new Map(this.sites().map(s => [s.id, s.name])));
  private readonly products = this.store.selectSignal(selectProducts);
  private readonly productsNameMap = computed(() => new Map(this.products().map(p => [p.id, p.name])));
  readonly breadcrumbsSignal = computed(() => {
    this.langChange();
    const sites = this.siteNameMap();
    const products = this.productsNameMap();
    return this.breadcrumbs().map(b => {
      if (b.productId) return { ...b, title: products.get(b.productId) ?? b.title };
      if (b.siteId) return { ...b, title: sites.get(b.siteId) ?? b.title };
      return { ...b, title: this.translate.instant(b.title) };
    });
  });

  private animationId: number | null = null;

  readonly tabs = computed<DsyTagConf[]>(() => {
    this.langChange();
    return [
      {
        id: RoutesName.PERFORMANCE,
        label: this.translate.instant('PD.TABS.PERFORMANCE'),
        shouldRenderSlot: false,
        onTabClickCallback: () => {
          this.router.navigate([RoutesName.PERFORMANCE]);
        },
      },
      // {
      //   id: RoutesName.ANALYTICS,
      //   label: this.translate.instant('PD.TABS.ANALYTICS'),
      //   shouldRenderSlot: false,
      //   onTabClickCallback: () => {
      //     this.router.navigate([RoutesName.ANALYTICS]);
      //   },
      // },
    ];
  });

  readonly switchItems = computed(() => {
    this.langChange();
    return [
      { title: this.translate.instant('PD.LABELS.PROGRAM'), data: { content: ProgramOrganizationType.Program } },
      { title: this.translate.instant('PD.LABELS.ORGANIZATION'), data: { content: ProgramOrganizationType.Organization } },
    ];
  });
  readonly viewMode = this.store.selectSignal(selectViewMode);

  // compute index for switch based on viewMode
  readonly switchIndex = computed(() => {
    switch (this.viewMode()) {
      case ProgramOrganizationType.Program:
        return 0;
      case ProgramOrganizationType.Organization:
        return 1;
      default:
        return 0;
    }
  });

  ngOnInit(): void {
    // Listen to route changes to keep tabs in sync
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        // When navigation happens, re-sync the tab state with the new URL
        this.syncTabWithUrl(event.urlAfterRedirects);
        // Also re-sync view mode state with URL
        this.syncViewModeWithUrl(event.urlAfterRedirects);
      });
    // On initial load or component re-creation, sync the tab with the current URL
    this.syncTabWithUrl(this.router.url);

    // Also sync view mode with URL
    this.syncViewModeWithUrl(this.router.url);

    this.animationId = requestAnimationFrame(() => {
      applyCustomBreadcrumbsCss();
    });
  }

  ngOnDestroy(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }
  }

  /**
   * Sets the active tab based on the current URL.
   * This makes the component's state reactive to the route.
   */
  private syncTabWithUrl(url: string): void {
    const segments = this.urlSegments(url);
    const activeTabIndex = this.tabs().findIndex(tab => segments.includes(tab.id as string));

    // If a matching tab is found, update the store.
    // The check for -1 prevents errors if the URL doesn't match any tab.
    if (activeTabIndex !== -1) {
      this.store.dispatch(setActiveTab({ tabIndex: activeTabIndex }));
    }
  }

  /**
   * Sets the active view mode based on the current URL.
   * This makes the component's state reactive to the route.
   */
  private syncViewModeWithUrl(url: string): void {
    const segments = this.urlSegments(url);
    let viewMode: ProgramOrganizationType | null = null;

    if (segments.includes(RoutesName.PROGRAM)) {
      viewMode = ProgramOrganizationType.Program;
    } else if (segments.includes(RoutesName.ORGANIZATION)) {
      viewMode = ProgramOrganizationType.Organization;
    }

    if (viewMode === null || viewMode === this.viewMode()) return;
    this.store.dispatch(setViewMode({ viewMode }));
  }

  private urlSegments(url: string): string[] {
    return url.split(/[?#]/, 1)[0].split('/').filter(Boolean);
  }

  /**
   * Handles url update when switch is toggled.
   * @param data The data associated with the selected switch option.
   */
  readonly handleSwitch = (data: { content: ProgramOrganizationType }) => {
    let path = '';

    switch (data.content) {
      case ProgramOrganizationType.Program:
        path = `/${RoutesName.PROGRAM}`;
        break;
      case ProgramOrganizationType.Organization:
        path = `/${RoutesName.ORGANIZATION}`;
        break;
    }

    this.router.navigate([path]);
  };

  onClickBreadcrumb = (url: string) => {
    const tree = this.router.parseUrl(url);
    tree.queryParams = this.router.parseUrl(this.router.url).queryParams;
    this.router.navigateByUrl(tree);
  };
}
